import { useMemo } from 'react';

import type { Parameters } from '@provider-portal/common/api/types';
import {
  useIsAccountOnboarding,
  useIsAccountOnboardingObservable,
} from '@provider-portal/common/experiments/account';
import {
  useTargetingParameters,
  useTargetingParametersObservable,
} from '@provider-portal/common/experiments/targeting';
import { useIsDesktop } from '@provider-portal/common/styles/useThemeHooks';

export const isWithMenuEditorControlValue = false;

export const SSU_EXPERIMENT_EVENTS = {
  LOADED: 'menu_editor_experiment:assigned',
  GOTO_MENU_EDITOR: 'menu_editor_experiment:cta_clicked',
  RETURN_TO_ONBOARDING: 'menu_editor_experiment:complete_signup_clicked',
  MENU_EDITOR_PUBLISHED: 'menu_editor_experiment:menu_published',
};

export interface MenuEditorParams {
  isAccountOnboarding: boolean;
  isWithMenuEditor: boolean;
  isDesktop: boolean;
}

export function shouldEnableMenuEditor(params: MenuEditorParams): boolean {
  return params.isAccountOnboarding && params.isDesktop && params.isWithMenuEditor;
}

export function useProviderOnboardingParameters(): NonNullable<Parameters['provider_onboarding']> {
  return useTargetingParameters().provider_onboarding ?? {};
}

export function useWithMenuEditor(): boolean {
  return useProviderOnboardingParameters().withMenuEditor ?? isWithMenuEditorControlValue;
}

export function useMenuEditorExperimentEnabled(): boolean {
  return useProviderOnboardingParameters().withMenuEditor !== undefined;
}

export function useWithMenuEditorObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'withMenuEditor',
    isWithMenuEditorControlValue,
  );
}

export function useShouldEnableMenuEditorParams(): MenuEditorParams {
  const isAccountOnboarding = useIsAccountOnboarding();
  const isWithMenuEditor = useWithMenuEditor();
  const isDesktop = useIsDesktop();

  return useMemo(() => {
    return {
      isAccountOnboarding,
      isWithMenuEditor,
      isDesktop,
    };
  }, [isAccountOnboarding, isDesktop, isWithMenuEditor]);
}

export function useShouldEnableMenuEditorObservableParams(): MenuEditorParams {
  const isAccountOnboarding = useIsAccountOnboardingObservable();
  const isWithMenuEditor = useWithMenuEditorObservable();
  const isDesktop = useIsDesktop();

  return useMemo(() => {
    return {
      isAccountOnboarding,
      isWithMenuEditor,
      isDesktop,
    };
  }, [isAccountOnboarding, isDesktop, isWithMenuEditor]);
}

export function useShouldEnableMenuEditor(): boolean {
  return shouldEnableMenuEditor(useShouldEnableMenuEditorParams());
}

export function useShouldEnableMenuEditorObservable(): boolean {
  return shouldEnableMenuEditor(useShouldEnableMenuEditorObservableParams());
}
